import React, {
  useMemo,
} from "react";

import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  FaExclamationTriangle,
  FaInfoCircle,
  FaLightbulb,
  FaStar,
} from "react-icons/fa";

const CALLOUT_CONFIG = {
  TIP: {
    label: "Astuce",
    icon: <FaLightbulb />,
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-100",
    iconClassName:
      "bg-emerald-500 text-white",
  },

  WARNING: {
    label: "Attention",
    icon: <FaExclamationTriangle />,
    className:
      "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100",
    iconClassName:
      "bg-amber-500 text-white",
  },

  IMPORTANT: {
    label: "Important",
    icon: <FaStar />,
    className:
      "border-violet-200 bg-violet-50 text-violet-950 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-100",
    iconClassName:
      "bg-violet-600 text-white",
  },

  NOTE: {
    label: "Bon à savoir",
    icon: <FaInfoCircle />,
    className:
      "border-sky-200 bg-sky-50 text-sky-950 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-100",
    iconClassName:
      "bg-sky-500 text-white",
  },
};

function parseMarkdownBlocks(content) {
  const lines =
    String(content || "").split("\n");

  const blocks = [];
  let markdownLines = [];

  const flushMarkdown = () => {
    const value =
      markdownLines.join("\n").trim();

    if (value) {
      blocks.push({
        type: "markdown",
        content: value,
      });
    }

    markdownLines = [];
  };

  let index = 0;

  while (index < lines.length) {
    const calloutMatch =
      lines[index].match(
        /^>\s*\[!(TIP|WARNING|IMPORTANT|NOTE)\]\s*$/i
      );

    if (!calloutMatch) {
      markdownLines.push(lines[index]);
      index += 1;
      continue;
    }

    flushMarkdown();

    const calloutType =
      calloutMatch[1].toUpperCase();

    const calloutLines = [];

    index += 1;

    while (
      index < lines.length &&
      /^>\s?/.test(lines[index])
    ) {
      calloutLines.push(
        lines[index].replace(
          /^>\s?/,
          ""
        )
      );

      index += 1;
    }

    blocks.push({
      type: "callout",
      calloutType,
      content:
        calloutLines.join("\n").trim(),
    });
  }

  flushMarkdown();

  return blocks;
}

export default function TutorialContentRenderer({
  content,
  format = "texte",
}) {
  const cleanHtml = useMemo(
    () =>
      DOMPurify.sanitize(
        String(content || ""),
        {
          USE_PROFILES: {
            html: true,
          },
        }
      ),
    [content]
  );

  if (!content) {
    return null;
  }

  if (format === "html") {
    return (
      <div
        className="tutorial-html text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-[15px]"
        dangerouslySetInnerHTML={{
          __html: cleanHtml,
        }}
      />
    );
  }

  if (format === "markdown") {
    const blocks =
      parseMarkdownBlocks(content);

    return (
      <div className="space-y-4">
        {blocks.map(
          (block, index) => {
            if (
              block.type ===
              "callout"
            ) {
              return (
                <Callout
                  key={index}
                  type={
                    block.calloutType
                  }
                  content={
                    block.content
                  }
                />
              );
            }

            return (
              <MarkdownContent
                key={index}
                content={
                  block.content
                }
              />
            );
          }
        )}
      </div>
    );
  }

  return (
    <SimpleTextContent
      content={content}
    />
  );
}

function SimpleTextContent({
  content,
}) {
  const blocks = String(content)
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  return (
    <div className="space-y-4 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-[15px]">
      {blocks.map((block, index) => {
        const lines = block
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean);

        const isBulletList =
          lines.length > 0 &&
          lines.every((line) =>
            /^[-*]\s+/.test(line)
          );

        const isNumberedList =
          lines.length > 0 &&
          lines.every((line) =>
            /^\d+[.)]\s+/.test(line)
          );

        if (isBulletList) {
          return (
            <ul
              key={index}
              className="space-y-2 pl-1"
            >
              {lines.map(
                (line, lineIndex) => (
                  <li
                    key={lineIndex}
                    className="flex gap-3"
                  >
                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />

                    <span>
                      {line.replace(
                        /^[-*]\s+/,
                        ""
                      )}
                    </span>
                  </li>
                )
              )}
            </ul>
          );
        }

        if (isNumberedList) {
          return (
            <ol
              key={index}
              className="space-y-2"
            >
              {lines.map(
                (line, lineIndex) => (
                  <li
                    key={lineIndex}
                    className="flex gap-3"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-xs font-bold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                      {lineIndex + 1}
                    </span>

                    <span>
                      {line.replace(
                        /^\d+[.)]\s+/,
                        ""
                      )}
                    </span>
                  </li>
                )
              )}
            </ol>
          );
        }

        return (
          <p key={index}>
            {lines.map(
              (line, lineIndex) => (
                <React.Fragment
                  key={lineIndex}
                >
                  {line}

                  {lineIndex <
                  lines.length - 1 ? (
                    <br />
                  ) : null}
                </React.Fragment>
              )
            )}
          </p>
        );
      })}
    </div>
  );
}

function MarkdownContent({
  content,
}) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={MARKDOWN_COMPONENTS}
    >
      {content}
    </ReactMarkdown>
  );
}

function Callout({
  type,
  content,
}) {
  const config =
    CALLOUT_CONFIG[type] ||
    CALLOUT_CONFIG.NOTE;

  return (
    <aside
      className={`rounded-[20px] border p-4 ${config.className}`}
    >
      <div className="flex gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${config.iconClassName}`}
        >
          {config.icon}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 font-semibold">
            {config.label}
          </div>

          <MarkdownContent
            content={content}
          />
        </div>
      </div>
    </aside>
  );
}

const MARKDOWN_COMPONENTS = {
  h2: ({ children }) => (
    <h2 className="mb-3 mt-6 text-xl font-bold text-slate-900 first:mt-0 dark:text-white">
      {children}
    </h2>
  ),

  h3: ({ children }) => (
    <h3 className="mb-2 mt-5 text-lg font-semibold text-slate-900 dark:text-white">
      {children}
    </h3>
  ),

  p: ({ children }) => (
    <p className="my-3 text-sm leading-7 text-slate-600 first:mt-0 last:mb-0 dark:text-slate-300 sm:text-[15px]">
      {children}
    </p>
  ),

  ul: ({ children }) => (
    <ul className="my-4 list-disc space-y-2 pl-6 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-[15px]">
      {children}
    </ul>
  ),

  ol: ({ children }) => (
    <ol className="my-4 list-decimal space-y-2 pl-6 text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-[15px]">
      {children}
    </ol>
  ),

  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900 dark:text-white">
      {children}
    </strong>
  ),

  a: ({ href, children }) => {
    const isExternal =
      /^https?:\/\//i.test(
        href || ""
      );

    return (
      <a
        href={href}
        target={
          isExternal
            ? "_blank"
            : undefined
        }
        rel={
          isExternal
            ? "noopener noreferrer"
            : undefined
        }
        className="font-semibold text-violet-700 underline decoration-violet-300 underline-offset-4 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-200"
      >
        {children}
      </a>
    );
  },

  blockquote: ({ children }) => (
    <blockquote className="my-4 rounded-r-2xl border-l-4 border-violet-500 bg-violet-50 px-4 py-3 dark:bg-violet-950/30">
      {children}
    </blockquote>
  ),

  code: ({ children }) => (
    <code className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[0.9em] text-violet-700 dark:bg-slate-800 dark:text-violet-300">
      {children}
    </code>
  ),
};